class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
      this.showB2g1fToast();
    });
  }

  showB2g1fToast() {
    const itemCount = parseInt(document.querySelector('.cart-count-bubble span[aria-hidden]')?.textContent) || 0;
    if (itemCount >= 3) return;
    const existing = document.getElementById('b2g1f-toast');
    if (existing) existing.remove();
    const items = 3 - itemCount;
    const toast = document.createElement('div');
    toast.id = 'b2g1f-toast';
    toast.setAttribute('style', 'position:fixed;bottom:20px;left:50%;transform:translateX(-50%) translateY(100px);z-index:9999;background:#b8860b;color:#fff;padding:10px 20px;border-radius:8px;font-size:13px;font-weight:600;box-shadow:0 4px 12px rgba(0,0,0,0.15);transition:transform 0.3s ease;white-space:nowrap;');
    toast.textContent = items > 0 ? `Add ${items} more ${items === 1 ? 'item' : 'items'} to get your 3rd FREE!` : 'Buy 2 Get 1 Free applied!';
    document.body.appendChild(toast);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => { toast.style.transform = 'translateX(-50%) translateY(0)'; });
    });
    setTimeout(() => {
      toast.style.transform = 'translateX(-50%) translateY(100px)';
      setTimeout(() => toast.remove(), 350);
    }, 3500);
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

// Pair-upgrade button ("It's a matching set — get both"). Delegated from the
// document because the drawer is re-rendered with innerHTML, which never runs
// an inline <script>, so a handler rendered inside the drawer was dead after
// every add-to-cart. The single is removed by line key, not line index:
// Shopify puts a newly added line first, which shifts every index by one.
document.addEventListener('click', async (event) => {
  const btn = event.target.closest('.hf-set-upgrade');
  if (!btn || btn.disabled) return;
  btn.disabled = true;
  btn.textContent = '...';

  const post = (url, body) =>
    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });

  let added;
  try {
    // Carry the line's properties over or we lose the personalisation.
    added = await post(`${routes.cart_add_url}.js`, {
      id: Number(btn.dataset.variant),
      quantity: Number(btn.dataset.quantity) || 1,
      properties: JSON.parse(btn.dataset.properties || '{}'),
    });
  } catch (e) {}
  if (!added || !added.ok) {
    // Nothing changed in the cart, so just offer the button again.
    btn.disabled = false;
    btn.textContent = 'Add';
    return;
  }

  try {
    const drawer = document.querySelector('cart-drawer');
    const changed = await post(`${routes.cart_change_url}.js`, {
      id: btn.dataset.key,
      quantity: 0,
      sections: drawer ? drawer.getSectionsToRender().map((section) => section.id) : [],
      sections_url: window.location.pathname,
    });
    if (!changed.ok) throw new Error('remove failed');

    const state = await changed.json();
    if (drawer && state.sections) {
      drawer.renderContents(state);
    } else {
      window.location.reload();
    }
  } catch (e) {
    // The pair is in the cart but the single may be too; reload so the drawer shows the truth.
    window.location.reload();
  }
});

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
